import React, { useCallback, useContext, useMemo } from 'react';

import InlineDialog from 'components/InlineDialog';
import { BonusOverviewContext } from 'components/DriverAX/providers/BonusOverviewProvider';
import { WeeklyBonusOverrideContext } from 'components/DriverAX/providers/WeeklyBonusOverrideProvider';
import SelectedEntityContext from 'components/PerformanceBoards/contexts/SelectedEntityContext';
import useValueFormatters from 'hooks/useValueFormatters';
import usePopup from 'hooks/usePopup';
import Button from 'kingpin/atoms/Button';

import SlideOutModeHeader from '../../components/SlideOutModeHeader';
import ConfirmOverrideDialog from './ConfirmOverrideDialog';
import { OverridesBadge, OverridesRemovedBadge } from './OverrideModeActions';

/**
 * Override-mode header actions for the per-week fact-table override flow. Mirrors
 * OverrideModeActions but reads its counts/pay-change/confirm from
 * WeeklyBonusOverrideContext: the badge shows the cumulative per-week override
 * count, and Confirm fans out one PUT per changed week (shared note + reason
 * codes) via onWeeklyOverridesConfirmed.
 */
const WeeklyOverrideModeActions = () => {
  const { exitOverrideMode } = useContext(BonusOverviewContext);
  const { selectedEntity } = useContext(SelectedEntityContext);
  const {
    changedWeekCount,
    stagedOverrideCount,
    cumulativeOverrideCount,
    stagedRemovalCount,
    isAnyPreviewLoading,
    aggregatePayChange,
    breakdown,
    isNoteRequired,
    onWeeklyOverridesConfirmed,
    bonusProgram,
  } = useContext(WeeklyBonusOverrideContext);
  const { formatField } = useValueFormatters();
  const { isOpen, open, close } = usePopup();

  // Each breakdown line formats with its reward's own field (currency for `*Pay`,
  // plain for `*Points`); the total falls back to `totalPay`.
  const formatPayValue = useCallback(
    (value: number, field?: string): string =>
      bonusProgram
        ? String(
            formatField({
              field: field ?? 'totalPay',
              value,
              dataset: bonusProgram.dataset,
            }),
          )
        : `${value}`,
    [bonusProgram, formatField],
  );

  const payChangeText = useMemo(() => {
    if (!aggregatePayChange || !bonusProgram) {
      return undefined;
    }
    const actual = formatField({
      field: 'totalPay',
      value: aggregatePayChange.actualPay,
      dataset: bonusProgram.dataset,
    });
    const preview = formatField({
      field: 'totalPay',
      value: aggregatePayChange.previewPay,
      dataset: bonusProgram.dataset,
    });
    return `${actual} -> ${preview}`;
  }, [aggregatePayChange, bonusProgram, formatField]);

  return (
    <SlideOutModeHeader
      title={'Override Bonus'}
      driverName={selectedEntity ?? 'Driver'}
      status={
        cumulativeOverrideCount > 0 || stagedRemovalCount > 0 ? (
          <>
            <OverridesBadge
              overridesCount={cumulativeOverrideCount}
              hideDivider
            />
            <OverridesRemovedBadge removedCount={stagedRemovalCount} />
          </>
        ) : undefined
      }
      actions={
        <>
          <Button
            size={'Medium'}
            type={'Secondary'}
            label={'Undo & Cancel'}
            onClick={exitOverrideMode}
          />
          <InlineDialog
            content={
              <ConfirmOverrideDialog
                close={close}
                isNoteRequired={isNoteRequired}
                onConfirm={onWeeklyOverridesConfirmed}
                payChangeText={payChangeText}
                reasonCodeProgramId={bonusProgram?.id}
                appliesToWeeks={changedWeekCount}
                breakdown={breakdown}
                formatPayValue={formatPayValue}
              />
            }
            isOpen={isOpen}
            onClose={close}
            hasArrow
          >
            <Button
              size={'Medium'}
              type={'Primary'}
              label={'Confirm Override(s)'}
              onClick={open}
              isDisabled={stagedOverrideCount === 0 || isAnyPreviewLoading}
            />
          </InlineDialog>
        </>
      }
    />
  );
};

export default WeeklyOverrideModeActions;
